import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import AjouterIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SupprimerIcon from '@mui/icons-material/PersonRemoveOutlined';
import LienIcon from '@mui/icons-material/Link';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { detacherAthlete, nomAthlete, useMoi, type AthleteRattache } from '../Auth/moi.ts';
import ModeleSeanceEditeur from '../Preferences/ModeleSeanceEditeur.tsx';
import FuseauHoraireEditeur from '../Preferences/FuseauHoraireEditeur.tsx';
import AjustementRecupEditeur from '../Preferences/AjustementRecupEditeur.tsx';
import EditeurParametresAllures from '../Allures/EditeurParametresAllures.tsx';
import ApercuAthlete from './ApercuAthlete.tsx';
import InviterAthlete from './InviterAthlete.tsx';
import LienInvitation from './LienInvitation.tsx';
import AvatarAthlete from './AvatarAthlete.tsx';

// Onglets du panneau d'un athlete (l'onglet courant est dans l'adresse,
// ex : /athletes/<id>?onglet=modeles, pour pouvoir y renvoyer directement).
const ONGLETS = [
  { cle: 'apercu', libelle: 'Aperçu' },
  { cle: 'allures', libelle: 'Allures' },
  { cle: 'recuperations', libelle: 'Récupérations' },
  { cle: 'modeles', libelle: 'Modèles de séance' },
  { cle: 'fuseau', libelle: 'Fuseau horaire' },
] as const;
type CleOnglet = (typeof ONGLETS)[number]['cle'];

/** Statut d'un athlete : invitation, premiere connexion, intervals.icu. */
function Statut({ athlete }: { athlete: AthleteRattache }) {
  if (athlete.enAttente) return <Chip size="small" color="warning" variant="outlined" label="Invitation en attente" />;
  if (!athlete.ssoUserId) return <Chip size="small" color="warning" variant="outlined" label="Première connexion attendue" />;
  return athlete.icuConfigure
    ? <Chip size="small" color="success" variant="outlined" label="intervals.icu relié" />
    : <Chip size="small" variant="outlined" label="intervals.icu non relié" />;
}

/**
 * Page Athletes du coach : la liste de ses athletes (et invitations) en
 * barre laterale, et le panneau de l'athlete choisi : apercu, modeles de
 * seance, allures, recuperations, fuseau horaire.
 */
export default function Athletes() {
  const moi = useMoi();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [athletes, setAthletes] = React.useState<AthleteRattache[] | null>(null);
  const [inviter, setInviter] = React.useState(false);
  const [lien, setLien] = React.useState(false);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(null);

  React.useEffect(() => {
    if (moi) setAthletes(moi.athletes);
  }, [moi]);

  const athlete: AthleteRattache | null = (athletes ?? []).find((a) => a.id === id) ?? null;
  // Panneau complet une fois l'invitation acceptee et l'athlete connecte.
  const actif = !!athlete && !athlete.enAttente && !!athlete.ssoUserId;
  const onglet: CleOnglet = ONGLETS.find((o) => o.cle === params.get('onglet'))?.cle ?? 'apercu';

  const choisir = (a: AthleteRattache) => {
    setMessage(null);
    navigate(`/athletes/${encodeURIComponent(a.id)}${onglet !== 'apercu' ? `?onglet=${onglet}` : ''}`);
  };

  const retirer = async (a: AthleteRattache) => {
    const question = a.enAttente
      ? `Annuler l'invitation envoyée à ${a.email} ?`
      : `Ne plus suivre ${nomAthlete(a)} ? Son calendrier est conservé.`;
    if (!window.confirm(question)) return;
    try {
      setAthletes(await detacherAthlete(a.id));
      setMessage(null);
      navigate('/athletes', { replace: true });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Action impossible : ' + (e as Error).message });
    }
  };

  const barreLaterale = (
    <Paper variant="outlined" sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, alignSelf: 'flex-start' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography variant="h6" component="h2">Mes athlètes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<LienIcon />} onClick={() => setLien(true)}>
            Lien
          </Button>
          <Button size="small" variant="contained" startIcon={<AjouterIcon />} onClick={() => setInviter(true)}>
            Inviter
          </Button>
        </Box>
      </Box>
      <Divider />
      {athletes && athletes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Aucun athlète pour l'instant : invite-le par l'adresse de son compte Google, ou transmets-lui ton lien d'invitation.
        </Typography>
      )}
      <List dense disablePadding aria-label="Mes athlètes">
        {(athletes ?? []).map((a) => (
          <ListItemButton key={a.id} selected={a.id === id} onClick={() => choisir(a)} divider>
            <ListItemAvatar sx={{ minWidth: 48 }}><AvatarAthlete athlete={a} /></ListItemAvatar>
            <ListItemText
              primary={nomAthlete(a)}
              secondary={<Box component="span" sx={{ display: 'block', mt: 0.5 }}><Statut athlete={a} /></Box>}
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );

  const panneau = athlete ? (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <AvatarAthlete athlete={athlete} taille={56} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h2">{nomAthlete(athlete)}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
            {athlete.nom && <Typography color="text.secondary">{athlete.email}</Typography>}
            <Statut athlete={athlete} />
          </Box>
        </Box>
      </Box>

      {actif ? (
        <>
          <Tabs
            value={onglet}
            onChange={(_e, valeur: CleOnglet) => setParams(valeur === 'apercu' ? {} : { onglet: valeur }, { replace: true })}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, '& .MuiTab-root': { textTransform: 'none', fontSize: '1rem' } }}
          >
            {ONGLETS.map((o) => <Tab key={o.cle} value={o.cle} label={o.libelle} />)}
          </Tabs>
          {/* Contenu de l'onglet (sans la marge haute du premier bloc) */}
          <Box sx={{ '& > :first-of-type': { mt: 0 }, '& > :first-of-type > h2:first-of-type': { mt: 0 } }}>
            {onglet === 'apercu' && <ApercuAthlete athlete={athlete} />}
            {onglet === 'allures' && <EditeurParametresAllures athlete={athlete} />}
            {onglet === 'recuperations' && <AjustementRecupEditeur athlete={athlete} />}
            {onglet === 'modeles' && <ModeleSeanceEditeur athlete={athlete} />}
            {onglet === 'fuseau' && <FuseauHoraireEditeur athlete={athlete} />}
          </Box>
        </>
      ) : (
        <Typography color="text.secondary">
          {athlete.enAttente
            ? `L'invitation lui sera proposée à sa prochaine connexion. Tu pourras établir son calendrier et régler ses paramètres une fois qu'il l'aura acceptée.`
            : `Il a accepté ton invitation mais ne s'est pas encore reconnecté : ses paramètres seront disponibles après sa prochaine connexion.`}
        </Typography>
      )}

      <Divider sx={{ mt: 5, mb: 2 }} />
      <Button color="error" variant="outlined" startIcon={<SupprimerIcon />} onClick={() => retirer(athlete)}>
        {athlete.enAttente ? "Annuler l'invitation" : 'Ne plus suivre cet athlète'}
      </Button>
    </Box>
  ) : (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography color="text.secondary">
        {id && athletes
          ? "Cet athlète n'est plus dans ta liste."
          : 'Choisis un athlète dans la liste pour suivre ses paramètres : modèles de séance, fuseau horaire, accès à son calendrier.'}
      </Typography>
    </Box>
  );

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 9, md: 10 } }}>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.texte}</Alert>}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mt: { xs: 1, md: 2 } }}>
        {barreLaterale}
        {panneau}
      </Box>
      <LienInvitation ouvert={lien} fermer={() => setLien(false)} />
      <InviterAthlete
        ouvert={inviter}
        fermer={() => setInviter(false)}
        invite={(liste, email) => {
          setAthletes(liste);
          setMessage({ severity: 'success', texte: `Invitation envoyée à ${email} : elle lui sera proposée à sa prochaine connexion.` });
          const nouveau = liste.find((a) => a.email.toLowerCase() === email.toLowerCase());
          if (nouveau) navigate(`/athletes/${encodeURIComponent(nouveau.id)}`);
        }}
      />
    </Box>
  );
}
